/**
 * SwipeHome — Typed API client.
 *
 * Replaces the legacy `api(method, path, body)` wrapper with a generic
 * version. The legacy signature is preserved as an overload so existing
 * call sites continue to work during the file-by-file migration.
 *
 * Usage:
 *   const listings = await apiGet<Page<Listing>>('/listings');
 *   const user     = await api<User>('GET', '/users/me'); // legacy
 */

import { state } from './state.js';
import type { ApiError } from './types/api.js';

// ── Base URL auto-detection ──────────────────────────────────────────

export const API: string = (() => {
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return 'http://localhost:3001/api';
  if (host.includes('staging')) return 'https://swipehome-staging.up.railway.app/api';
  return 'https://swipehome-production.up.railway.app/api';
})();

// ── Auth-failure handler (set by main.ts to avoid circular imports) ──

type AuthFailHandler = (targetScreen: 'login' | 'gateKey') => void;

let _authFailHandler: AuthFailHandler | null = null;

export function setAuthFailHandler(fn: AuthFailHandler): void {
  _authFailHandler = fn;
}

// ── Core request helper ──────────────────────────────────────────────

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

async function request<T>(method: HttpMethod, path: string, body?: unknown): Promise<T> {
  const opts: RequestInit = {
    method,
    headers: { 'Content-Type': 'application/json' },
  };
  if (state.token) {
    (opts.headers as Record<string, string>)['Authorization'] = `Bearer ${state.token}`;
  }
  if (body !== undefined) {
    opts.body = JSON.stringify(body);
  }

  const res = await fetch(`${API}${path}`, opts);

  let data: unknown;
  try {
    data = await res.json();
  } catch {
    if (!res.ok) throw new Error(`Server error (${res.status})`);
    throw new Error('Invalid server response');
  }

  if (res.status === 401) {
    state.token = null;
    state.user = null;
    localStorage.removeItem('swipehome_token');
    localStorage.removeItem('swipehome_user');
    document.body.style.overflow = '';
    const target: 'login' | 'gateKey' =
      sessionStorage.getItem('sh_gate_unlocked') === '1' ? 'login' : 'gateKey';
    if (_authFailHandler) _authFailHandler(target);
    throw new Error('Session expired');
  }

  if (!res.ok) {
    const err = data as ApiError;
    throw new Error(err?.detail || err?.error || err?.message || 'Request failed');
  }

  return data as T;
}

// ── Convenience helpers ──────────────────────────────────────────────

export function apiGet<T>(path: string): Promise<T> {
  return request<T>('GET', path);
}

export function apiPost<T>(path: string, body?: unknown): Promise<T> {
  return request<T>('POST', path, body);
}

export function apiPut<T>(path: string, body?: unknown): Promise<T> {
  return request<T>('PUT', path, body);
}

export function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  return request<T>('PATCH', path, body);
}

export function apiDelete<T>(path: string): Promise<T> {
  return request<T>('DELETE', path);
}

// ── Legacy-compatible signature ──────────────────────────────────────

/**
 * Back-compat wrapper so existing `api('GET', '/foo')` call sites work
 * unchanged. Prefer the named helpers (`apiGet<T>`, etc.) in new code.
 */
export function api<T = unknown>(method: HttpMethod, path: string, body?: unknown): Promise<T> {
  return request<T>(method, path, body);
}
